import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export type UserRole = 'admin' | 'gov' | 'suppliers' | 'auditors' | 'retailers' | 'public'

export interface User {
  id: string
  username: string
  role: UserRole
  name: string
  organization?: string
}

interface AuthContextType {
  user: User | null
  login: (username: string, password: string, role: UserRole) => Promise<boolean>
  logout: () => void
  isAuthenticated: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

// Mock user database - in real app this would be API calls
const mockUsers: Record<string, { password: string; user: User }> = {
  'admin': {
    password: 'admin123',
    user: {
      id: '1',
      username: 'admin',
      role: 'admin',
      name: 'System Administrator',
      organization: 'RecycleChain Admin'
    }
  },
  'gov_user': {
    password: 'gov123',
    user: {
      id: '2',
      username: 'gov_user',
      role: 'gov',
      name: 'John Smith',
      organization: 'Hong Kong Environmental Protection Department'
    }
  },
  'enterprise_supplier': {
    password: 'supplier123',
    user: {
      id: '3',
      username: 'enterprise_supplier',
      role: 'suppliers',
      name: 'Mary Chen',
      organization: 'OneChain Corporate Client'
    }
  },
  'auditor_user': {
    password: 'audit123',
    user: {
      id: '4',
      username: 'auditor_user',
      role: 'auditors',
      name: 'David Wong',
      organization: 'Independent Environmental Auditors Ltd'
    }
  },
  'retailer_user': {
    password: 'retail123',
    user: {
      id: '5',
      username: 'retailer_user',
      role: 'retailers',
      name: 'Lisa Zhang',
      organization: 'Green Retail Solutions'
    }
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const savedUser = localStorage.getItem('currentUser')
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser))
      } catch (error) {
        console.error('Error parsing saved user:', error)
        localStorage.removeItem('currentUser')
      }
    }
    setIsLoading(false)
  }, [])

  const login = async (username: string, password: string, role: UserRole): Promise<boolean> => {
    setIsLoading(true)
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Check credentials
    const userRecord = mockUsers[username]
    if (userRecord && userRecord.password === password && userRecord.user.role === role) {
      const authenticatedUser = userRecord.user
      setUser(authenticatedUser)
      localStorage.setItem('currentUser', JSON.stringify(authenticatedUser))
      setIsLoading(false)
      return true
    }
    
    setIsLoading(false)
    return false
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('currentUser')
  }

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isLoading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
